'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface FormState {
  name: string;
  company: string;
  email: string;
  contactNumber: string;
  note: string;
}

const initialForm: FormState = {
  name: '',
  company: '',
  email: '',
  contactNumber: '',
  note: '',
};

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [form, setForm] = useState<FormState>(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-up, .reveal-left').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 90);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const supabase = createClient();
      const { error: sbError } = await supabase.from('contact_submissions').insert({
        name: form.name,
        company: form.company || null,
        email: form.email,
        contact_number: form.contactNumber || null,
        note: form.note || null,
      });

      if (sbError) {
        setError('Something went wrong. Please try again.');
      } else {
        setSuccess(true);
        setForm(initialForm);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full bg-transparent border border-furnace/30 text-bone placeholder-bone-dim/50 font-mono text-sm px-4 py-3 focus:outline-none focus:border-amber transition-colors';

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="spoke-section py-20 lg:py-28 heat-bg"
    >
      <div className="section-accent" />

      <div className="max-w-screen-xl mx-auto px-6 lg:px-14">
        {/* Header */}
        <div className="mb-12">
          <div className="reveal-up">
            <span className="spoke-index">06 // CONTACT</span>
          </div>
          <h2 className="reveal-up delay-1 font-sans font-900 text-[clamp(2rem,5vw,3.5rem)] leading-none tracking-tight text-bone mt-3 uppercase">
            Contact Us
          </h2>
          <p className="reveal-up delay-2 font-mono text-sm text-amber mt-2 tracking-wide">
            We are happy to hear from you.
          </p>
          <p className="reveal-up delay-2 font-sans text-[0.95rem] leading-relaxed text-bone-dim mt-4 max-w-xl">
            Reach out to us via email or use the form below.
          </p>

          {/* Social Media Links */}
          <div className="reveal-up delay-3 flex items-center gap-5 mt-6">
            <a
              href="https://www.instagram.com/moddsys_inc/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-bone-dim hover:text-amber transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@moddsys"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="text-bone-dim hover:text-amber transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/moddsys-modular-design-systems/?viewAsMember=true"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://www.linkedin.com/company/moddsys-modular-design-systems/?viewAsMember=true',
                  'linkedin_popup',
                  'width=1000,height=700,scrollbars=yes,resizable=yes'
                );
              }}
              className="text-bone-dim hover:text-amber transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Form */}
        <div className="reveal-up delay-2 max-w-2xl">
          {success ? (
            <div className="border border-furnace/40 px-6 py-8 text-center">
              <p className="font-mono text-sm text-amber tracking-wide uppercase mb-2">Message Sent</p>
              <p className="font-sans text-bone-dim text-sm">
                Thank you for reaching out. We will be in touch shortly.
              </p>
              <button
                onClick={() => setSuccess(false)}
                className="mt-6 btn-furnace"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block font-mono text-xs text-bone-dim uppercase tracking-widest mb-2">
                    Name <span className="text-amber">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs text-bone-dim uppercase tracking-widest mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Your company"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block font-mono text-xs text-bone-dim uppercase tracking-widest mb-2">
                    Email <span className="text-amber">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs text-bone-dim uppercase tracking-widest mb-2">
                    Contact Number
                  </label>
                  <input
                    type="tel"
                    name="contactNumber"
                    value={form.contactNumber}
                    onChange={handleChange}
                    placeholder="+971 xx xxx xxxx"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block font-mono text-xs text-bone-dim uppercase tracking-widest mb-2">
                  Note
                </label>
                <textarea
                  name="note"
                  value={form.note}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Tell us about your project or enquiry…"
                  className={`${inputClass} resize-none`}
                />
              </div>

              {error && (
                <p className="font-mono text-xs text-red-400 mb-4">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-furnace disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
